/**
 * FREE SHIPPING BAR - cart-drawer + cart page compatible (v7)
 *
 * INSTALL:
 * 1. Upload to Assets as: wsl-freeshipping-init.js
 * 2. In cart-drawer.liquid, just before </cart-drawer>:
 *      <script src="{{ 'wsl-freeshipping-init.js' | asset_url }}" defer></script>
 * 3. In theme.liquid, just before </body>:
 *      <script>
 *        localStorage.setItem('userCountry', {{ localization.country.name | json }});
 *        localStorage.setItem('userCurrency', {{ cart.currency.iso_code | json }});
 *      </script>
 */

(function () {
  'use strict';

  var POLL_INTERVAL_MS = 100;
  var POLL_TIMEOUT_MS  = 5000;
  var _observer        = null;
  var _isUpdating      = false;
  var _qtyJustUpdated  = false;
  var _qtyRevertTimer  = null;

  // ── Wait for localStorage ──────────────────────────────────────────────────
  function waitForUserData() {
    return new Promise(function (resolve, reject) {
      var start = Date.now();
      var timer = setInterval(function () {
        var country  = (localStorage.getItem('userCountry')  || '').trim();
        var currency = (localStorage.getItem('userCurrency') || '').trim();
        if (country && currency) {
          clearInterval(timer);
          resolve({ userCountry: country, userCurrency: currency });
        } else if (Date.now() - start >= POLL_TIMEOUT_MS) {
          clearInterval(timer);
          reject(new Error('[FreeShipping] Timeout — userCountry/userCurrency not in localStorage'));
        }
      }, POLL_INTERVAL_MS);
    });
  }

  // ── Fetch live cart total from /cart.js ────────────────────────────────────
  function fetchLiveCartTotal() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    })
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      return (cart.total_price || 0) / 100; // cents → dollars
    });
  }

  // ── Shared render logic ────────────────────────────────────────────────────
  // messageSelector  : CSS selector for the message element
  // progressSelector : CSS selector for the progress bar element
  function renderBarToSelectors(cartTotal, userCountry, userCurrency, messageSelector, progressSelector) {
    var messageEl      = document.querySelector(messageSelector);
    var progressEl     = document.querySelector(progressSelector);
    var colorEl        = document.querySelector('#color-freeshipping');
    var defaultShipEl  = document.querySelector('#default-freeshipping');
    var currencyEl     = document.querySelector('#cart-currency-code');
    var freeShipEls    = document.querySelectorAll('.free-shipping');

    if (!messageEl) return;

    var storeCurrency = (currencyEl ? (currencyEl.getAttribute('data-value') || '').trim() : '');

    if (storeCurrency !== 'USD' && storeCurrency !== userCurrency) {
      messageEl.textContent = '';
      if (progressEl) progressEl.setAttribute('style', '');
      return;
    }

    var symbolMap = { USD: '$', EUR: '€', GBP: '£', AUD: '$', CAD: '$', COP: '$' };
    var symbol    = symbolMap[storeCurrency] || symbolMap[userCurrency] || '$';

    var matched = false;

    freeShipEls.forEach(function (el) {
      var dataCountry = (el.getAttribute('data-country') || '').toLowerCase();
      if (dataCountry !== userCountry.toLowerCase()) return;

      matched = true;

      var rawValue  = (storeCurrency === 'USD')
        ? el.getAttribute('data-value')
        : el.getAttribute('data-value-currency');

      var threshold = rawValue ? parseFloat(rawValue.replace(/[^0-9.]/g, '')) : 0;
      var remaining = threshold - cartTotal;
      var percent   = threshold > 0 ? Math.min((cartTotal / threshold) * 100, 100) : 0;
      var barColor  = (colorEl ? colorEl.getAttribute('data-colorfreeshipping') : '') || '#71c35a';

      messageEl.textContent = remaining > 0
        ? symbol + remaining.toFixed(2) + ' AWAY FROM FREE SHIPPING!'
        : 'YOU HAVE FREE SHIPPING!!!';

      if (progressEl) {
        progressEl.setAttribute('style',
          '--progress-bar-color: ' + barColor + '; --shipping-percent: ' + percent.toFixed(2) + '%;'
        );
      }
    });

    // Default fallback (USD only)
    if (!matched && defaultShipEl
        && defaultShipEl.getAttribute('data-enable') === 'true'
        && storeCurrency === 'USD') {

      var defRaw       = (defaultShipEl.getAttribute('data-default') || '').replace(/[^0-9.]/g, '');
      var defThreshold = defRaw ? parseFloat(defRaw) : 0;
      var defRemaining = defThreshold - cartTotal;
      var defPercent   = defThreshold > 0 ? Math.min((cartTotal / defThreshold) * 100, 100) : 0;
      var defColor     = (colorEl ? colorEl.getAttribute('data-colorfreeshipping') : '') || '#71c35a';

      messageEl.textContent = defRemaining > 0
        ? symbol + defRemaining.toFixed(2) + ' AWAY FROM FREE SHIPPING!'
        : 'YOU HAVE FREE SHIPPING!!!';

      if (progressEl) {
        progressEl.setAttribute('style',
          '--progress-bar-color: ' + defColor + '; --shipping-percent: ' + defPercent.toFixed(2) + '%;'
        );
      }
    }
  }

  // ── Render to ALL bar instances on the page ────────────────────────────────
  // cart-drawer uses : .free-shipping-bar__message  + .free-shipping-bar__progress
  // cart page uses   : .free-shipping-bar__message-main + .free-shipping-bar__progress-main
  function renderBar(cartTotal, userCountry, userCurrency) {
    // Cart drawer bar
    var gamificationEl = document.querySelector('.side-cart__gamification');
    renderBarToSelectors(cartTotal, userCountry, userCurrency,
      '.free-shipping-bar__message',
      '.free-shipping-bar__progress'
    );

    // Cart page bar (main cart)
    renderBarToSelectors(cartTotal, userCountry, userCurrency,
      '.free-shipping-bar__message-main',
      '.free-shipping-bar__progress-main'
    );

    // Hide gamification block if no match (handled inside renderBarToSelectors
    // for drawer, but we keep this as a safety net)
    if (gamificationEl && !document.querySelector('.free-shipping-bar__message')?.textContent) {
      gamificationEl.style.display = 'none';
    }
  }

  // ── Core update: fetch live total then render ──────────────────────────────
  function updateFreeShippingBar() {
    if (_isUpdating) return;
    _isUpdating = true;

    waitForUserData()
      .then(function (data) {
        return fetchLiveCartTotal().then(function (cartTotal) {
          renderBar(cartTotal, data.userCountry, data.userCurrency);
        });
      })
      .catch(function (err) { console.error('[FreeShipping]', err.message); })
      .finally(function () {
        setTimeout(function () { _isUpdating = false; }, 300);
      });
  }

  // ── Quantity button handler ────────────────────────────────────────────────
  // Handles <a class="quantity-up/down" href="/cart/change?...">
  // Works on BOTH cart-drawer AND cart page — same HTML structure
  function handleQuantityClick(link) {
    var href = link.getAttribute('href');
    if (!href || href.indexOf('/cart/change') === -1) return;

    var jsonHref = href.replace('/cart/change', '/cart/change.js');

    // Block MutationObserver from reverting our update
    _qtyJustUpdated = true;
    clearTimeout(_qtyRevertTimer);

    fetch(jsonHref, { method: 'GET', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var cartTotal = (cart.total_price || 0) / 100;
        waitForUserData().then(function (data) {
          renderBar(cartTotal, data.userCountry, data.userCurrency);
          // Re-enable observer after 2s (theme DOM re-render is done by then)
          _qtyRevertTimer = setTimeout(function () {
            _qtyJustUpdated = false;
          }, 2000);
        });
      })
      .catch(function () {
        _qtyJustUpdated = false;
        setTimeout(updateFreeShippingBar, 800);
      });
  }

  // ── Bind quantity clicks via event delegation on document ─────────────────
  // Covers both cart-drawer AND cart page with one listener
  function bindQuantityButtons() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a.quantity-up, a.quantity-down');
      if (!link) return;
      handleQuantityClick(link);
    });
  }

  // ── Observe cart-drawer for open/close and item add/remove ─────────────────
  function observeCartDrawer() {
    if (_observer) return;

    var cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    var debounceTimer = null;

    _observer = new MutationObserver(function (mutations) {
      if (_isUpdating) return;
      if (_qtyJustUpdated) return; // skip — qty button already updated bar

      var shouldUpdate = false;

      mutations.forEach(function (m) {
        if (m.type === 'attributes'
            && (m.attributeName === 'open' || m.attributeName === 'aria-hidden')
            && m.target === cartDrawer) {
          shouldUpdate = true;
        }
        if (m.type === 'childList') {
          var insideBar = m.target.closest && m.target.closest('.free-shipping-bar');
          if (!insideBar) shouldUpdate = true;
        }
      });

      if (!shouldUpdate) return;

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        updateFreeShippingBar();
      }, 200);
    });

    _observer.observe(cartDrawer, {
      attributes: true,
      childList: true,
      subtree: true
    });
  }

  // ── Init ───────────────────────────────────────────────────────────────────
  function init() {
    updateFreeShippingBar();
    observeCartDrawer();
    bindQuantityButtons();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.wslUpdateFreeShipping = updateFreeShippingBar;

})();